import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import JSZip from 'jszip';
import { ApiService } from '../../core/services/api.service';

@Component({
  selector: 'app-detail-transaction',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './detail-transaction.component.html',
  styleUrl: './detail-transaction.component.scss'
})
export class DetailTransactionComponent implements OnInit {
  sessionData: any = null;
  isLoading = true;
  isDownloading = false;

  // Modal State Properties
  isModalOpen = false;
  selectedPhotoUrl = '';
  selectedPhotoName = '';

  // FAQ Accordion State
  activeFaqIndex: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private apiService: ApiService
  ) {}

  ngOnInit(): void {
    const segment = this.route.snapshot.paramMap.get('segment');
    if (segment) {
      this.fetchSession(segment);
    } else {
      this.isLoading = false;
    }
  }

  fetchSession(segment: string) {
    this.apiService.get<any>(`/public/photo-sessions/${segment}`).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.sessionData = res.data;
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching session', err);
        this.isLoading = false;
      }
    });
  }


  // Open full-screen photo modal
  openModal(url: string, name: string) {
    this.selectedPhotoUrl = url;
    this.selectedPhotoName = name;
    this.isModalOpen = true;
    document.body.style.overflow = 'hidden';
  }

  // Close full-screen photo modal
  closeModal() {
    this.isModalOpen = false;
    this.selectedPhotoUrl = '';
    this.selectedPhotoName = '';
    document.body.style.overflow = '';
  }

  // Toggle FAQ Accordion panel
  toggleFaq(index: number) {
    this.activeFaqIndex = this.activeFaqIndex === index ? null : index;
  }

  private getCorsFreeUrl(url: string): string {
    if (!url) return url;
    // For local development on localhost, route storage requests through Angular dev server proxy to prevent CORS/403 errors
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      if (url.startsWith('https://pb-storage.temporary.my.id')) {
        return url.replace('https://pb-storage.temporary.my.id', '/pb-storage');
      }
    }
    return url;
  }

  // Download photo safely (specifically handles iOS native sharing fallback)
  downloadPhoto(url: string, filename: string) {
    const targetUrl = this.getCorsFreeUrl(url);
    fetch(targetUrl)
      .catch(() => fetch(`https://corsproxy.io/?${encodeURIComponent(url)}`))
      .then(response => {
        if (!response.ok) throw new Error('HTTP error ' + response.status);
        return response.blob();
      })
      .then(blob => {
        const file = new File([blob], filename, { type: blob.type });
        // Check if Web Share API is available and can share this file (native behavior on iOS)
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          navigator.share({
            files: [file],
            title: filename,
          }).catch(err => {
            console.warn('Sharing cancelled or failed, triggering standard blob download', err);
            this.triggerBlobDownload(blob, filename);
          });
        } else {
          this.triggerBlobDownload(blob, filename);
        }
      })
      .catch(error => {
        console.error('Blob fetch failed (possibly CORS), falling back to opening in a new tab', error);
        // Fallback: Open in new tab so user can long-press to save (standard on iOS)
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
  }

  private triggerBlobDownload(blob: Blob, filename: string) {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 200);
  }

  private async fetchFileBlob(fileUrl: string): Promise<Blob> {
    if (!fileUrl) throw new Error('Empty URL');
    const corsFreeUrl = this.getCorsFreeUrl(fileUrl);

    if (corsFreeUrl !== fileUrl) {
      try {
        const res = await fetch(corsFreeUrl);
        if (res.ok) return await res.blob();
      } catch (e) {
        console.warn('CorsFreeUrl fetch failed, falling back to original URL', e);
      }
    }

    const res2 = await fetch(fileUrl);
    if (!res2.ok) throw new Error(`HTTP error ${res2.status}`);
    return await res2.blob();
  }

  private async downloadZipFiles(
    files: { url: string; name: string }[],
    zipFilename: string,
  ) {
    if (!files || files.length === 0) return;

    this.isDownloading = true;
    const zip = new JSZip();

    try {
      for (const file of files) {
        try {
          const blob = await this.fetchFileBlob(file.url);
          zip.file(file.name, blob);
        } catch (err) {
          console.error(`Direct fetch failed for ${file.name}:`, err);
        }
      }

      if (Object.keys(zip.files).length === 0) {
        this.isDownloading = false;
        return;
      }

      const zipBlob = await zip.generateAsync({
        type: 'blob',
        mimeType: 'application/zip',
      });

      this.triggerBlobDownload(zipBlob, zipFilename);
    } catch (err) {
      console.error('Error generating zip file:', err);
    } finally {
      this.isDownloading = false;
    }
  }

  // Download all moments as zip file
  downloadAllMoments() {
    if (!this.sessionData) return;

    const filesToDownload: { url: string; name: string }[] = [];

    // Add framed photos
    if (this.sessionData.framed_photos) {
      this.sessionData.framed_photos.forEach((photo: any, index: number) => {
        filesToDownload.push({ url: photo.url, name: `framed_photo_${index + 1}.jpg` });
      });
    }

    // Add soft files
    if (this.sessionData.soft_files) {
      this.sessionData.soft_files.forEach((file: any, index: number) => {
        filesToDownload.push({ url: file.url, name: file.name || `soft_file_${index + 1}.jpg` });
      });
    }

    if (filesToDownload.length === 0) return;
    this.downloadZipFiles(filesToDownload, 'all_photos.zip');
  }
}

